<script lang="ts" setup>
import SkrChip from '@/components/SkrChip.vue'

const props = defineProps<{ status: string; size: 'small' | 'large' }>()

const { t } = useI18n()

const statusMap = computed(() => ({
  // Signer statuses
  pending: {
    color: 'warning',
    text: t('home.pending'),
  },
  declined: {
    color: 'error',
    text: t('home.declined'),
  },
  withdrawn: {
    color: 'error',
    text: t('home.withdrawn'),
  },
  queued: {
    color: 'grey',
    text: t('home.queued'),
  },
  signed: {
    color: 'success',
    text: t('home.signed'),
  },
  // SR statuses
  to_sign: {
    text: t('home.need_to_sign'),
    textColor: 'to-sign',
    backgroundColor: 'to-sign-bg',
  },
  waiting: {
    text: t('home.waiting'),
    textColor: 'waiting',
    backgroundColor: 'waiting-bg',
  },
  cancelled: {
    text: t('home.cancelled'),
    textColor: 'cancelled',
    backgroundColor: 'cancelled-bg',
  },
  completed: {
    text: t('home.completed'),
    textColor: 'completed',
    backgroundColor: 'completed-bg',
  },
  draft: {
    color: 'grey-dark',
    text: t('home.draft'),
  },
  undefined: {
    color: 'grey',
    text: 'undefined',
  },
}))
const statusValue = computed(() => statusMap.value[props.status] || statusMap.value.undefined)
const valid = computed(() => statusValue.value.text !== 'undefined')
const backgroundColor = computed(() => statusValue.value.backgroundColor ?? '')
const color = computed(() => (backgroundColor.value ? 'transparent' : statusValue.value.color))
const textColorClass = computed(() =>
  statusValue.value.textColor ? `status-chip--text-color-${statusValue.value.textColor}` : ''
)
const sizeClass = computed(() => `status-chip--${props.size}`)
</script>

<template>
  <skr-chip
    v-if="valid"
    class="status_chip"
    :class="[textColorClass, sizeClass]"
    :color="color"
    :bg-color="backgroundColor"
    :size="size"
    :label="true"
    data-cy="status_chip"
    >{{ statusValue.text }}</skr-chip
  >
</template>

<style lang="scss" scoped>
.v-chip {
  font-weight: 400 !important;
  &--label {
    border-radius: 6px;
    padding: 6px;
  }
}

$sizes: (
  small: 28px,
  large: 40px,
);

@each $name, $height in $sizes {
  .status-chip--#{$name} {
    height: $height !important;
  }
}

$status-colors: (
  to-sign: $c-to-sign-status,
  waiting: $c-waiting-status,
  cancelled: $c-cancelled-status,
  completed: $c-completed-status,
);

@each $status, $color in $status-colors {
  .status-chip--text-color-#{$status} :deep(.v-chip__content) {
    color: $color;
  }
}
</style>
